export enum StoredValueFields {
  PIN = 'pin',
  CARD = 'card',
}

/**
 * TEAM-14906. Two of these carried a factual error and three typos.
 *
 * The old VALID_WITH_TIP_OR_FEES told the diner "Taxes, Fees, ad Tip cannot be paid
 * for with a Gift Card." Sales tax IS paid by the card, by design: chowly-api's
 * `SquareGiftCardPayment.calculate_usable_amount` sets the ceiling at
 * `subtotal + sales_tax - discount`, with a comment stating the intent outright.
 * So the message contradicted the order summary printed directly beneath it: the
 * card had paid the sales tax the message said it could not. (Found by a
 * verification pass, not by a diner report — this ticket records no evidence of how
 * often the message was actually read.)
 *
 * The replacements name what the card COVERS rather than stating a rule about what
 * it cannot, which is deliberate on two counts:
 *
 *  - "toward", never "to". The branch that shows this fires whenever
 *    `giftCardAmount > subtotal` (form.tsx:137), but the ceiling is
 *    `min(balance, subtotal + sales_tax - discount)` — so a card that runs out
 *    partway through the tax, or one pulled under the tax line by a discount, still
 *    lands here with the tax only PARTLY paid. "Applied to your food and sales tax"
 *    would be false in exactly those states, which is the same class of over-claim
 *    this ticket exists to remove. "toward" is true in every one of them.
 *  - "which may include tip and delivery fees" stays true where a closed list would
 *    not. A service fee in `fees[]` is also still owed and is neither tip nor
 *    delivery, and a pickup order has no delivery fee at all.
 *  - TEAM-14491 widened the Valutec ceiling to tip and conveyance for partners on
 *    the Toast payment provider, evaluated at runtime (`gift_card_calculator.rb`
 *    `covers_fees_and_tips?`). Square is exempt only because its
 *    `calculate_usable_amount` swallows that kwarg with `**_`. Copy shaped as
 *    "tip cannot be paid with a gift card" becomes false the day that changes;
 *    copy that names coverage does not.
 *
 * "sales tax", never "taxes and fees": the card covers `sales_tax` only, not the fees in `fees[]`.
 *
 * NOTE: the two quoted server strings below are KEYS, not copy (API message to form field).
 * TEAM-14906 froze them, so nothing here may rename them. No app code reads them by key:
 * `storedValueError` holds resolved diner copy, not one of these keys (`types/paymentInfo.ts`).
 */
export const storedValueMessages = {
  'PIN field is required.': StoredValueFields.PIN,
  'Oops, gift card not found': StoredValueFields.CARD,
  VALID: 'Gift Card Applied!',
  VALID_WITH_BALANCE:
    'Gift Card Applied. Please enter payment information to pay for the balance of the order.',
  VALID_WITH_TIP_OR_FEES:
    'Gift Card Applied toward your food and sales tax. Please enter payment information to pay for the remaining balance, which may include tip and delivery fees.',
};

// Whitelisted API errors
export const storedValueErrors = {
  PIN_REQUIRED: 'PIN field is required.',
  CAPTCHA: 'Oops, we could not validate this gift card. Please try again.',
  DEFAULT: 'Oops, gift card not found',
  INVALID: 'Invalid Gift Card, Please Try Again.',
};
